import { Component, OnInit, OnDestroy, Inject, NgZone, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { SystemTypeUtils } from 'app/incidents/services/system.type.util';
import { DialogResult } from 'app/shared/common/models/enums/dialog-result/dialog.result';
import { Spinner } from 'app/shared/common/components/spinner-modal/spinner';
import { Device } from 'app/shared/common/models/device';
import { CommuneService } from 'app/client-settings/services/commune.service';
import { LuxDataTechnicalService } from 'app/shared/common/services/ld.technical.service';
import { ObjUtils } from 'app/shared/common/services/obj.utils';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, forkJoin, Subject, Subscription } from 'rxjs';
import { map, debounceTime } from 'rxjs/operators';
import { GoogleMapModalDialogData } from './google-map-modal-dialog.data';
import {
  CommuneTO,
  GeoLocationResponseV2ControlCabinetLocationDataV2,
  GeoLocationResponseV2LightingPointLocationDataV2,
  IncidentStatus,
  SystemType,
} from '../../../api/generated/v2/incident';

@Component({
    selector: 'app-google-map-modal',
    templateUrl: './google-map-modal.component.html',
    styleUrls: ['./google-map-modal.component.scss'],
    standalone: false
})
export class GoogleMapModalComponent implements OnInit, OnDestroy {
  public reportForm: UntypedFormGroup = new UntypedFormGroup({
    location: new UntypedFormGroup({
      street: new UntypedFormControl('', Validators.maxLength(50)),
      houseNo: new UntypedFormControl('', Validators.maxLength(5)),
      zipCode: new UntypedFormControl('', Validators.maxLength(5)),
      city: new UntypedFormControl('', Validators.maxLength(50)),
      cityDistrict: new UntypedFormControl('', Validators.maxLength(50)),
      lightPointNo: new UntypedFormControl('', Validators.maxLength(17)),
      controlCabinetNo: new UntypedFormControl('', Validators.maxLength(12)),
      allNo: new UntypedFormControl('', Validators.maxLength(17)),
      netarea: new UntypedFormControl(''),
      isLightningPointCheck: new UntypedFormControl(false),
      isControlCabinetCheck: new UntypedFormControl(false),
      systemType: new UntypedFormControl('', Validators.required),
      customerScope: new UntypedFormControl('')
    })
  });

  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;
  public infoContent1: string;
  public infoContent2: string;
  public infoContent3: string;

  public devices: Device[];

  public selectedDevice: Device;

  public markers: any[] = [];
  public markersAll: any[] = [];
  public lpMarkers: any[] = [];
  public ccMarkers: any[] = [];
  markersLimit = 800;

  public loaded = false;
  public loading = false;
  public totalCount = 0;
  public pageSize = 0;
  /**
   * Subject emitting viewport bounds on map movement/zoom to query the /by-bounds endpoint (Ticket 5576273).
   */
  private boundsChanged$ = new Subject<{ swLat: number; swLon: number; neLat: number; neLon: number }>();
  private boundsSubscription: Subscription;

  // colors for points on map
  private readonly colorTiefenblau: string = '#000099';
  private readonly colorHorizontorange: string = '#ff9900';
  private readonly colorSelectedBlue: string = '#004eff';
  private readonly colorControlCabinet: string = '#FF7FBC';

  // BW center
  private readonly initCenterLat: number = 48.53223951904002;
  private readonly initCenterLng: number = 8.923548500000003;
  // BW zoom level
  private readonly initZoomLevel: number = 8;

  private readonly  geoDegreesLimit: number = 1;

  private readonly inProgressIncidentStates: IncidentStatus[] = [
    IncidentStatus.New,
    IncidentStatus.ApprovalRequiredByNetzeBw,
    IncidentStatus.OrderPlaced,
    IncidentStatus.Overdue,
    IncidentStatus.CheckFeedback,
    IncidentStatus.FollowUpMeasuresMaterialProcurement,
    IncidentStatus.FollowUpMeasuresCableTestVan,
    IncidentStatus.FollowUpMeasuresMoreDeficiencies,
    IncidentStatus.InternalClarification,
    IncidentStatus.Unrepairable,
    IncidentStatus.CheckFeedbackFollowUpMeasures,
    IncidentStatus.FollowUpMeasureInProgress,
    IncidentStatus.ImminentDanger,
    IncidentStatus.ImminentDangerLiesAtSalesDepartment,
    IncidentStatus.OrderMaterialOpen,
    IncidentStatus.OrderMaterialClosed,
    IncidentStatus.Deficit
  ];

  public isLightningPointChecked = false;
  public isControlCabinetChecked = false;

  public readonly systemTypes: any[] = SystemTypeUtils.SYSTEM_TYPES.filter(elem => !!elem.active);

  public center: google.maps.LatLngLiteral = { lat: this.initCenterLat, lng: this.initCenterLng, };
  public zoom: number = this.initZoomLevel;
  googleAutocompleteService;
  googleGeocoder;

  communeCache: CommuneTO[] = [];

  autocompletedCityOptions$: Observable<CommuneTO[]> = this.reportForm
    .get('location').get('city').valueChanges
    .pipe(
      debounceTime(300),
      map((city: string) => {
        if (!city?.trim()) { return []; }
        return this.communeCache.filter(c =>
          c.town?.toLowerCase().startsWith(city.trim().toLowerCase())
        );
      }),
    );

  constructor(
    public dialogRef: MatDialogRef<GoogleMapModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GoogleMapModalDialogData,
    protected sanitizer: DomSanitizer,
    protected iconRegistry: MatIconRegistry,
    private readonly communeService: CommuneService,
    private readonly ldtSvc: LuxDataTechnicalService,
    private readonly logger: NGXLogger,
    private readonly dialog: MatDialog,
    private readonly _ngZone: NgZone,
  ) {
      iconRegistry.addSvgIcon('place', sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/place.svg'));

      this.reportForm.get('location').get('isLightningPointCheck').valueChanges.subscribe((lpChecked: boolean) => {
        const cc = this.reportForm.get('location').get('isControlCabinetCheck');
        const lp = this.reportForm.get('location').get('isLightningPointCheck');

        const controlCabinetNo = this.reportForm.get('location').get('controlCabinetNo');
        const lightPointNo = this.reportForm.get('location').get('lightPointNo');

        lightPointNo.setValue('');
        controlCabinetNo.setValue('');

        this.isLightningPointChecked = lpChecked;

        if (lpChecked && !cc.value) {
          lp.disable({ emitEvent: false });
        } else if (!lpChecked && cc.value) {
          cc.disable({ emitEvent: false });
        } else {
          cc.enable({ emitEvent: false });
          lp.enable({ emitEvent: false });
        }
      });

      this.reportForm.get('location').get('isControlCabinetCheck').valueChanges.subscribe((ccChecked: boolean) => {
        const cc = this.reportForm.get('location').get('isControlCabinetCheck');
        const lp = this.reportForm.get('location').get('isLightningPointCheck');

        const controlCabinetNo = this.reportForm.get('location').get('controlCabinetNo');
        const lightPointNo = this.reportForm.get('location').get('lightPointNo');

        lightPointNo.setValue('');
        controlCabinetNo.setValue('');

        this.isControlCabinetChecked = ccChecked;

        if (ccChecked && !lp.value) {
          cc.disable({ emitEvent: false });
        } else if (!ccChecked && lp.value) {
          lp.disable({ emitEvent: false });
        } else {
          cc.enable({ emitEvent: false });
          lp.enable({ emitEvent: false });
        }
      });
  }

  ngOnInit() {
    this.boundsSubscription = this.boundsChanged$
      .pipe(debounceTime(400))
      .subscribe(bounds => {
        const customerScope = this.reportForm.value.location.customerScope;
        if (!customerScope || !this.isLightningPointChecked) {
          return;
        }
        this.ldtSvc.getLightpointsByBounds(
          customerScope,
          bounds.swLat,
          bounds.swLon,
          bounds.neLat,
          bounds.neLon
        ).subscribe(res => {
          if (!res || !res.data) {
            return;
          }
          res.data.forEach(elem => {
            if (!elem.latitude || !elem.longitude) {
              return;
            }
            const exists = this.devices?.some(d => d.technicalNo === elem.number);
            if (!exists) {
              const device: Device = new Device(
                [elem.longitude, elem.latitude, elem.number].join(''),
                elem.number,
                elem.longitude,
                elem.latitude,
                elem.street,
                elem.houseNumber,
                elem.zipCode,
                elem.city,
                elem.district,
                elem.cabinetNumber,
                elem.customerScope,
                elem.netArea,
                elem.incident?.incidentStatus
              );
              this.devices = this.devices ? [...this.devices, device] : [device];
              const marker = this.createMarker(device, false);
              this.lpMarkers.push(marker);
              this.markersAll.push(marker);
            }
          });
          this.applyVisibleMarkers(bounds.swLat, bounds.swLon, bounds.neLat, bounds.neLon);
        }, error => {
          this.logger.error('Error fetching lighting points by bounds', error);
        });
      });
    this.communeService.getAllCommunes().subscribe((communes: CommuneTO[]) => {
      this.communeCache = communes;
    });

    this.initFormValues();
    this.googleAutocompleteService = new google.maps.places.AutocompleteService();
    this.googleGeocoder = new google.maps.Geocoder();

    this.enableCorrectCheckbox();

    this.checkCityAutocomplete().subscribe(() => this.onSearchBtnClick());
  }

  private enableCorrectCheckbox() {
    const cc = this.reportForm.get('location').get('isControlCabinetCheck');
    const lp = this.reportForm.get('location').get('isLightningPointCheck');

    const lightningPointNo = this.reportForm.get('location').get('lightPointNo');
    const controlCabinetNo = this.reportForm.get('location').get('controlCabinetNo');

    const isCcValueSet = controlCabinetNo.value !== '' && controlCabinetNo.value !== null;
    const isLpValueSet = lightningPointNo.value !== '' && lightningPointNo.value !== null;

    if (isCcValueSet && !isLpValueSet) {
      this.isControlCabinetChecked = true;
      cc.setValue(true, { emitEvent: false });
      cc.disable({ emitEvent: false });
      lp.enable({ emitEvent: false });
    } else {
      this.isLightningPointChecked = true;
      controlCabinetNo.setValue('');
      lp.setValue(true, { emitEvent: false });
      lp.disable({ emitEvent: false });
      cc.enable({ emitEvent: false });
    }
  }

  ngOnDestroy() {
    this.boundsSubscription?.unsubscribe();
  }

  public onClsBtnClick(): void {
    this.close(DialogResult.CLOSED);
  }

  public onSearchBtnClick(): void {
    this.checkCityAutocomplete().subscribe(() => this.loadData());
  }

  public onTakeOverBtnClick(): void {
    this.close(DialogResult.TAKEOVER);
  }

  public openInfo(marker: MapMarker, infoText1: string, infoText2: string, infoText3: string): void {
    this.infoContent1 = infoText1;
    this.infoContent2 = infoText2;
    this.infoContent3 = infoText3;
    this.infoWindow.open(marker);
  }

  public selectObject(deviceId: string): void {
    // remove old selection if present
    if (!ObjUtils.isNullOrUndefined(this.selectedDevice)) {
      this.removeFromMarkers(this.selectedDevice);
      this.pushToMarkers(this.selectedDevice, false);
    }
    this.selectedDevice = this.devices.find(elem => elem.id === deviceId);
    this.setFormValuesFromSDevice(this.selectedDevice);
    this.syncSelectedDevice();
  }

  private setFormValuesFromSDevice(device: Device) {
    this.reportForm.patchValue({
      location: {
        street: device.street,
        houseNo: device.houseNo,
        zipCode: device.zipCode,
        city: device.city,
        cityDistrict: device.cityDistrict,
        lightPointNo: device.technicalNo,
        controlCabinetNo: device.technicalNo,
        netarea: device.netArea,
        customerScope: device.customerScope,
        allNo: device.technicalNo
      }
    });
  }

  public syncSelectedDevice() {
    if (!this.selectedDevice) { return; }
    const marker = this.markers.find(m => m.deviceId == this.selectedDevice.id);
    if (!marker || marker.selected) { return; }        // do not overwrite as it creates unwanted effect while panning
    this.removeFromMarkers(this.selectedDevice);
    this.pushToMarkers(this.selectedDevice, true);
  }

  private initFormValues(): void {
    if (ObjUtils.isNullOrUndefined(this.data)
    || ObjUtils.isNullOrUndefined(this.data.locationData)) {
      return;
    }
    const hasLightPoint: boolean =
      !ObjUtils.isNullOrUndefinedOrEmpty(this.data.locationData.lightPointNo);
    const hasControlCabinet: boolean =
      !ObjUtils.isNullOrUndefinedOrEmpty(this.data.locationData.controlCabinetNo);
    this.reportForm.patchValue({
      location: {
        street: this.data.locationData.street,
        houseNo: this.data.locationData.houseNo,
        zipCode: this.data.locationData.zipCode,
        city: this.data.locationData.city,
        cityDistrict: this.data.locationData.cityDistrict,
        lightPointNo: this.data.locationData.lightPointNo,
        controlCabinetNo: this.data.locationData.lightPointNo ? null : this.data.locationData.controlCabinetNo,
        allNo: '',
        netarea: this.data.locationData.netArea,
        systemType: hasControlCabinet && !hasLightPoint ?
          SystemType.ControlCabinetPoint : SystemType.LightingPoint,
        customerScope: this.data.locationData.customerScope
      }
    });
  }

  private pickUpFormData() {
    return {
      street: this.reportForm.value.location.street,
      houseNo: this.reportForm.value.location.houseNo,
      zipCode: this.reportForm.value.location.zipCode,
      city: this.reportForm.value.location.city,
      cityDistrict: undefined,
      allNo: this.reportForm.value.location.allNo,
      lightingNo: (this.isLightningPointChecked && this.reportForm.value.location.lightPointNo) ? this.reportForm.value.location.lightPointNo : null, // "" does not work
      controlCabinetNo: (this.isControlCabinetChecked && this.reportForm.value.location.controlCabinetNo) ? this.reportForm.value.location.controlCabinetNo : null, // "" does not work
    };
  }

  googleGeocodePlace(address: string): Observable<any> {
    return new Observable<any[]>(observer => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status: string) => {
        this._ngZone.run(() => {
          observer.next(results);
          observer.complete();
        });
      });
    });
  }

  public checkCityAutocomplete(event = null): Observable<any> {
    if (this.reportForm.value.location.allNo) {
      return of(false);
    }
    if (event?.relatedTarget?.tagName === 'MAT-OPTION') {
      // the input was blurred, but the user is still interacting with the component, they've simply
      // selected a mat-option
      return of(true);
    }

    const city = this.reportForm.value.location.city?.trim();
    const zipCode = this.reportForm.value.location.zipCode;
    const customerScope = this.sanitizeCustomerScope(this.reportForm.value.location.customerScope);

    if (!city) {
      this.onCommuneSelect(null);
      return of(false);
    }

    const communes = this.communeCache.filter(c =>
      c.town?.toLowerCase().startsWith(city.toLowerCase())
    );

    if (!communes.length) {
      this.onCommuneSelect(null);
      return of(false);
    }

    // city & ags already matching
    const exactFind = communes.find(_ => _.town.toLocaleLowerCase() === city.toLocaleLowerCase() && _.ags === customerScope);
    if (exactFind) {
      return of(true);
    }

    const cityFinds = communes.filter(_ => _.town.toLocaleLowerCase().startsWith(city.toLocaleLowerCase()));
    if (cityFinds.length === 1) {
      this.onCommuneSelect(cityFinds[0]);
    } else if (cityFinds.length > 1) {
      const find = cityFinds.find(_ => _.zipCode === zipCode);
      this.onCommuneSelect(find || cityFinds[0]);
    } else {
      this.onCommuneSelect(null);
    }

    return of(true);
  }

  private sanitizeCustomerScope(customerScope: string): string {
    if (!customerScope) { return customerScope; }
    if (!customerScope.match(/^(AGS|ZV)_/)) {
      customerScope = 'AGS_' + customerScope;
    }        // if no AGS or ZV prefix found, assume its AGS_
    return customerScope;
  }

  public onCommuneSelect(commune: CommuneTO | null) {
    if (commune == null) {
      this.reportForm.get('location').get('city').setErrors({ incorrect: true });
    } else {
      this.reportForm.get('location').get('city').patchValue(commune?.town);
      this.reportForm.get('location').get('customerScope').patchValue(this.sanitizeCustomerScope(commune?.ags));
      this.reportForm.get('location').get('zipCode').patchValue(commune?.zipCode);
    }
  }

  private async loadData(): Promise<any> {
    this.markers = [];
    this.markersAll = [];
    this.selectedDevice = null;
    const resArr: Device[] = [];
    const address = this.pickUpFormData();
    let customerScope = this.reportForm.value.location.customerScope;
    const city = this.reportForm.value.location.city;
    const zipCode = this.reportForm.value.location.zipCode;
    const id = this.isControlCabinetChecked && this.isLightningPointChecked ? this.reportForm.value.location.allNo :
      this.isControlCabinetChecked ? this.reportForm.value.location.controlCabinetNo : this.reportForm.value.location.lightPointNo;

    if (!customerScope && !id) {
      this.mapInteractionChanged();
      return;
    }

    const spinner = new Spinner();
    spinner.show(this.dialog, { message: `Lädt ${!!this.isControlCabinetChecked && this.isLightningPointChecked ? 'Leuchtstellen / Schal' : this.isControlCabinetChecked ? 'Schal' : 'Leuch'}tstellen...`});
    this.loading = true;

    // single light point search, no city/customerScope
    if (id && !customerScope) { await this.ldtSvc[this.getCorrectRequestMethodName()](id).toPromise().then(single => {
        if (single && !customerScope) {
          customerScope = single.customerScope;
        }
      }).catch(error => {
        this.logger.error(error.error.errorMessage);
        spinner.close();
        this.reportForm.get('location').get('lightPointNo')?.reset();
        this.reportForm.get('location').get('controlCabinetNo')?.reset();
        this.reportForm.get('location').get('allNo')?.reset();
        this.reportForm.get('location').updateValueAndValidity();
    });
    }

    if (!customerScope) {
      spinner.close();
      this.loading = false;
      this.mapInteractionChanged();
      return;
    }

    // Correct google address is Street, zipCode, City and then land/country
    const input = [
     zipCode, city, (this.adjustStreetName(address.street) + ' ' + address.houseNo).trim()
    ].filter(_ => _).join(' ');

    // tslint:disable-next-line:max-line-length
    const technicalNo = this.isControlCabinetChecked && this.isLightningPointChecked ? address.allNo : this.isControlCabinetChecked ? address.controlCabinetNo : address.lightingNo;

    const bounds = new google.maps.LatLngBounds();

    const obsGoogle = this.googleGeocodePlace(input);

    let obsLightingPoints: Observable<GeoLocationResponseV2LightingPointLocationDataV2>;
    let obsControlCabinetsPoints: Observable<GeoLocationResponseV2ControlCabinetLocationDataV2>;

    if (this.isControlCabinetChecked) {
      obsControlCabinetsPoints = this.ldtSvc.getControlCabinetGeolocation(customerScope);
    }

    if (this.isLightningPointChecked) {
      if (technicalNo) {
        // Direct search for a single lamp by technical number
        obsLightingPoints = this.ldtSvc.getLightpointsByCustomerScopeV2(customerScope, technicalNo);
      } else {
        // Performance optimization for big communes (Ticket 5576273):
        // Avoid downloading all 5,000-10,000+ lightpoints upfront during initial load.
        // Instead, viewport points are fetched dynamically via boundsChanged$ as the map view settles.
        obsLightingPoints = of({
          centerLatitude: 0,
          centerLongitude: 0,
          customerScope,
          data: [],
          totalCount: 0
        });
      }
    }

    let mapRequest: Observable<any>;
    if (obsControlCabinetsPoints && obsLightingPoints) {
      mapRequest = this.readAllData(obsLightingPoints, obsControlCabinetsPoints, obsGoogle);
    } else if (obsLightingPoints) {
      mapRequest  = this.readSingleData(obsLightingPoints, obsGoogle);
    } else {
      mapRequest  = this.readSingleData(obsControlCabinetsPoints, obsGoogle);
    }

    mapRequest
      .subscribe(([lightingPoints, googleAddress]) => {

        const correctMapAddress = this.getCorrectMapAddress(googleAddress, city, zipCode);

        const geoResult = lightingPoints;
        const ccMarkers: any[] = [];
        const lpMarkers: any[] = [];

        const hasCenter = geoResult.centerLatitude && geoResult.centerLongitude;
        geoResult.data
        .filter(elem => elem.latitude && elem.longitude &&
          (!hasCenter || (
            elem.latitude - geoResult.centerLatitude >= - this.geoDegreesLimit &&
            elem.latitude - geoResult.centerLatitude <= this.geoDegreesLimit &&
            elem.longitude - geoResult.centerLongitude >= -this.geoDegreesLimit &&
            elem.longitude - geoResult.centerLongitude <= this.geoDegreesLimit
          )))
        .forEach((elem) => {
          const device: Device = new Device(
            [elem.longitude, elem.latitude, elem.number].join(''),
            elem.number,
            elem.longitude,
            elem.latitude,
            elem.street,
            elem.houseNumber,
            elem.zipCode,
            elem.city,
            elem.district,
            elem.cabinetNumber,
            elem.customerScope,
            elem.netArea,
            elem.incident?.incidentStatus
          );
          resArr.push(device);

          elem.cabinetNumber ? lpMarkers.push(this.createMarker(device, false)) : ccMarkers.push(this.createMarker(device, false));
          bounds.extend(new google.maps.LatLng(elem.latitude, elem.longitude));
        });
        this.devices = resArr;
        this.lpMarkers = lpMarkers;
        this.ccMarkers = ccMarkers;
        this.markersAll = [...this.lpMarkers, ...this.ccMarkers];

        const device = technicalNo ? this.devices.find(elem => elem.technicalNo === technicalNo) : null;
        if (device) {
          this.selectObject(device.id);
          this.center = { lat: device.latitude, lng: device.longitude };
          this.zoom = 19;
        } else if (address.street?.trim()) {
          this.center = { lat: correctMapAddress.geometry.location.lat(), lng: correctMapAddress.geometry.location.lng() };
          this.zoom = 19;
        } else if (this.markersAll.length) {
          this.map.fitBounds(bounds, 5);
        } else {
          this.center = { lat: correctMapAddress.geometry.location.lat(), lng: correctMapAddress.geometry.location.lng() };
          this.zoom = 14;
        }

        this.loaded = true;
        this.totalCount = geoResult.totalCount;
        this.pageSize = geoResult.totalCount;
        this.loading = false;
        this.mapInteractionChanged();      // trigger map interaction change, usefull when zoom remains on the same position
        spinner.close();
      },
      (error: any) => {
        // case data could not be loaded
        this.logger.log(error);
        this.loaded = true;
        this.totalCount = 0;
        this.loading = false;
        this.mapInteractionChanged();      // trigger map interaction change, usefull when zoom remains on the same position
        spinner.close();
      });
  }

  updateValidators() {
    const lightPointNo = this.reportForm.get('location.lightPointNo');
    const controlCabinetNo = this.reportForm.get('location.controlCabinetNo');
    const allNo = this.reportForm.get('location.allNo');

    if (this.isLightningPointChecked && !this.isControlCabinetChecked) {
      // Only lightPointNo is checked
      lightPointNo?.setValidators([Validators.required]);
      controlCabinetNo?.clearValidators();
      allNo?.clearValidators();
    } else if (!this.isLightningPointChecked && this.isControlCabinetChecked) {
      // Only controlCabinetNo is checked
      controlCabinetNo?.setValidators([Validators.required]);
      lightPointNo?.clearValidators();
      allNo?.clearValidators();
    } else if (this.isLightningPointChecked && this.isControlCabinetChecked) {
      // Both are checked, so allNo should be validated
      allNo?.setValidators([Validators.required]);
      lightPointNo?.clearValidators();
      controlCabinetNo?.clearValidators();
    } else {
      // None of the fields are checked
      lightPointNo?.clearValidators();
      controlCabinetNo?.clearValidators();
      allNo?.clearValidators();
    }

    // Revalidate form controls after applying validators
    lightPointNo?.updateValueAndValidity();
    controlCabinetNo?.updateValueAndValidity();
    allNo?.updateValueAndValidity();
  }

  onCheckboxChange() {
    this.updateValidators();
  }

  private getCorrectMapAddress(googleAddress, city, zipCode): any {
    if (googleAddress.length === 1) {
      return googleAddress[0];
    }

    const foundCityAndZipCode = googleAddress.find(_ => _.formatted_address.includes(city) && _.formatted_address.includes(zipCode));
    if (foundCityAndZipCode) {
      return foundCityAndZipCode;
    }

    const foundCity = googleAddress.find(_ => _.formatted_address.includes(city) );
    if (foundCity) {
      return foundCity;
    }

    return googleAddress[0];
  }

  private readAllData(lpRequest: Observable<GeoLocationResponseV2LightingPointLocationDataV2>,
                      ccRequest: Observable<GeoLocationResponseV2ControlCabinetLocationDataV2>,
                      obsGoogle): Observable<any> {
    return forkJoin([lpRequest, ccRequest, obsGoogle])
      .pipe(
        map(([lpPoints, ccPoints, obsGoogleResponse]: any) => {
          const mergedLightningPoints: GeoLocationResponseV2ControlCabinetLocationDataV2 = {
            totalCount: (lpPoints?.totalCount || 0) + (ccPoints?.totalCount || 0),
            centerLatitude: lpPoints?.centerLatitude || ccPoints?.centerLatitude,
            centerLongitude: lpPoints?.centerLongitude || ccPoints?.centerLongitude,
            customerScope: lpPoints?.customerScope || ccPoints?.customerScope,
            data: [...(lpPoints?.data || []), ...(ccPoints?.data || [])]
          };
          return [mergedLightningPoints, obsGoogleResponse];
        })
      );
  }

  private readSingleData(dataRequest: Observable<GeoLocationResponseV2LightingPointLocationDataV2>, obsGoogle): Observable<any> {
    return forkJoin([dataRequest, obsGoogle]);
  }

  mapInteractionChanged() {
    if (!this.map || !this.map.getBounds()) { return; }

    const northEast = this.map.getBounds().getNorthEast();
    const southWest = this.map.getBounds().getSouthWest();
    const northEastLong = northEast.lng();
    const northEastLat = northEast.lat();
    const southWestLong = southWest.lng();
    const southWestLat = southWest.lat();

    this.boundsChanged$.next({
      swLat: southWestLat,
      swLon: southWestLong,
      neLat: northEastLat,
      neLon: northEastLong,
    });

    this.applyVisibleMarkers(southWestLat, southWestLong, northEastLat, northEastLong);
  }

  /**
   * Filters and renders visible markers on the map based on the current bounding box coordinates,
   * respecting the markersLimit threshold to prevent browser rendering lag.
   *
   * @param southWestLat - South-west corner latitude
   * @param southWestLong - South-west corner longitude
   * @param northEastLat - North-east corner latitude
   * @param northEastLong - North-east corner longitude
   */
  private applyVisibleMarkers(southWestLat: number, southWestLong: number, northEastLat: number, northEastLong: number) {
    const markersIn = [];
    this.markersAll.forEach(m => {
      if (southWestLong < m.position.lng && northEastLong > m.position.lng &&
        southWestLat < m.position.lat && northEastLat > m.position.lat
      ) {
        markersIn.push(m);
      }
    });

    const applyMarkers = markersIn.length > this.markersLimit ? [] : markersIn;

    applyMarkers.forEach(m => {
      if (!this.markers.find(_ => m.deviceId === _.deviceId)) {
        this.markers.push(m);
      }
    });

    this.markers
      .filter(m => !applyMarkers.find(_ => m.deviceId === _.deviceId))
      .forEach(rm => {
        const pos = this._indexOfMarker(this.markers, rm.deviceId);
        this.markers.splice(pos, 1);
      });

    this.syncSelectedDevice();
  }

  private adjustStreetName(street: string | undefined): string {
    if (!street) {
      return '';
    }
    return street.replace(/str\.?$/i, 'straße');
  }

  private _indexOfMarker(array, deviceId) {
      for (let x = 0; x < array.length; x++) {
          if (array[x].deviceId === deviceId) { return x; }
      }
      return null;
  }

  private pushToMarkers(device: Device, selected: boolean): void {
    this.markers.push(this.createMarker(device, selected));
  }

  private isNumberAControlCabinet(technicalNumber: string): boolean {
    const patternRegex = /^[A-Z]{5}-[A-Z]-\d{4}$/;
    return patternRegex.test(technicalNumber);
  }

  private isNumberALightningPoint(technicalNumber: string): boolean {
    const customPatternRegex = /^[A-Z]{5}-(SOLAR|PRIVA|\d{5})-\d{5}$/;
    return customPatternRegex.test(technicalNumber);
  }

  private setLightPointMarkerColor(device: Device): string {
    return this.inProgressIncidentStates.includes(device.incidentStatus) ? this.colorHorizontorange : this.colorTiefenblau;
  }

  private createMarker(device: Device, selected: boolean): any {
    const color = this.getMarkerColor(device, selected);
    const icon = this.createIcon(selected, color);
    const infoText1 = this.getInfoText1(device);
    const infoText2 = this.getInfoText2(device);
    const missingDataLabel = this.getMissingDataLabel(device);
    return {
      deviceId: device.id,
      position: {
        lat: device.latitude,
        lng: device.longitude,
      },
      infoText1: infoText2,
      infoText2: infoText1,
      infoText3: missingDataLabel,
      options: { icon },
      selected
    };
  }

  private createIcon(selected: boolean, color: string): any {
    return {
      path: selected ? google.maps.SymbolPath.BACKWARD_CLOSED_ARROW : google.maps.SymbolPath.CIRCLE,
      scale: 6,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: color,
      strokeOpacity: 0,
    };
  }

  private getMarkerColor(device: Device, selected: boolean): string {
    if (this.isNumberALightningPoint(device.technicalNo)) {
      return selected ? this.colorSelectedBlue : this.setLightPointMarkerColor(device);
    } else if (this.isNumberAControlCabinet(device.technicalNo)) {
      return selected ? this.colorSelectedBlue : this.colorControlCabinet;
    }

    return 'none';
  }

  private getInfoText1(device: Device): string {
    return `${this.isNumberALightningPoint(device.technicalNo) ? 'LeuchtenNr.:' : 'SchaltstellenNr.:'} ${device.technicalNo}`;
  }

  private getInfoText2(device: Device): string {
    const street = ObjUtils.isNullOrUndefinedOrEmpty(device.street) ? '' : device.street;
    const houseNo = ObjUtils.isNullOrUndefinedOrEmpty(device.houseNo) ? '' : device.houseNo;
    const zipCode = ObjUtils.isNullOrUndefinedOrEmpty(device.zipCode) ? '' : device.zipCode;
    const city = ObjUtils.isNullOrUndefinedOrEmpty(device.city) ? '' : device.city;
    const cityDistrict = ObjUtils.isNullOrUndefinedOrEmpty(device.cityDistrict) ? '' : `/${device.cityDistrict}`;

    return `${street} ${houseNo}, ${zipCode} ${city} ${cityDistrict}`;
  }

  private getMissingDataLabel(device: Device): string {
    return `
      ${ObjUtils.isNullOrUndefinedOrEmpty(device.street) ||
      ObjUtils.isNullOrUndefinedOrEmpty(device.houseNo) ||
      ObjUtils.isNullOrUndefinedOrEmpty(device.zipCode) ||
      ObjUtils.isNullOrUndefinedOrEmpty(device.city) ||
      ObjUtils.isNullOrUndefinedOrEmpty(device.zipCode) ? 'Daten in LuxData unvollständig hinterlegt' : ''}`;
  }

  private removeFromMarkers(device: Device): void {
    const index: number = this.markers.findIndex(elem => elem.deviceId === device.id);
    this.markers.splice(index, 1);
  }

  private close(dialogResult: DialogResult): void {
    this.data.dialogResult = dialogResult;
    this.readSelectedLocationValues();
    this.dialogRef.close(this.data);
  }

  private readSelectedLocationValues(): void {
    if (ObjUtils.isNullOrUndefined(this.selectedDevice)) {
      return;
    }
    this.data.locationData.street = this.selectedDevice.street;
    this.data.locationData.houseNo = this.selectedDevice.houseNo;
    this.data.locationData.zipCode = this.selectedDevice.zipCode;
    this.data.locationData.city = this.selectedDevice.city;
    this.data.locationData.cityDistrict = this.selectedDevice.cityDistrict;
    this.data.locationData.lightPointNo = this.isNumberALightningPoint(this.selectedDevice.technicalNo) ? this.selectedDevice.technicalNo : null;
    this.data.locationData.controlCabinetNo = this.isNumberAControlCabinet(this.selectedDevice.technicalNo) ? this.selectedDevice.technicalNo : this.selectedDevice.controlCabinetNo;
    this.data.locationData.customerScope = this.selectedDevice.customerScope;
    this.data.locationData.netArea = this.selectedDevice.netArea ? this.selectedDevice.netArea : this.data.locationData.netArea;
    this.data.locationData.houseNo = this.selectedDevice.houseNo ? this.selectedDevice.houseNo : this.data.locationData.houseNo;
  }

  private getCorrectRequestMethodName(): string {
    if (this.isLightningPointChecked && this.isControlCabinetChecked) {
      return this.isNumberAControlCabinet(this.reportForm.value.location.allNo) ? 'getControlCabinetDataById' : 'getLightpointDataById';
    }
    return this.isControlCabinetChecked ? 'getControlCabinetDataById' : 'getLightpointDataById';
  }
}
